// SchedulerViewSlot.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './SchedulerViewSlot.css';

/**
 * SchedulerViewSlot
 *
 * Renders a single clickable scheduler grid slot.
 *
 * @param {number} height - Slot row height in pixels.
 * @param {boolean} isHighlighted - Whether the slot is actively highlighted by drag or move state.
 * @param {boolean} isHour - Whether the slot starts on the hour.
 * @param {boolean} isInteractive - Whether the slot should present as clickable.
 * @param {function} onClick - Click handler for the slot.
 * @param {function} onDragOver - Drag-over handler for the slot.
 * @param {function} onDrop - Drop handler for the slot.
 * @param {string} resourceKey - Resource key for the slot column.
 * @param {object} row - Time row metadata for the slot.
 *
 * @returns {ReactNode} The rendered scheduler slot row.
 */
const SchedulerViewSlot = ({
    height,
    isHighlighted = false,
    isHour = false,
    isInteractive = false,
    onClick,
    onDragOver,
    onDrop,
    resourceKey,
    row,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [hovered, setHovered] = useState(false);
    // ----- RENDER ----- //

    return (
        <div
          className={`scheduler-view-slot-row${isHour ? ' scheduler-view-slot-row-hour' : ''}`}
          style={{
            backgroundColor: isHighlighted || hovered ? theme.backgroundHover : 'transparent',
            cursor: isInteractive ? 'pointer' : 'default',
            height,
          }}
          data-resource-key={resourceKey}
          data-row-key={row?.key || ''}
          data-start-minutes={row?.minutes}
          onClick={onClick}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
    );
};

// Dev Tools
SchedulerViewSlot.displayName = 'SchedulerViewSlot';

export default SchedulerViewSlot;
